import React from "react"
import { useApp } from "../context/AppContext"
import { Icon, useT, FAKE } from "../shared"
import BrandLogo, { useOrgName, PoweredByGladhy } from "../shared/BrandLogo"
import { Stat, TableWrap, Btn } from "../shared/components"
import { claimCertificate, sendVerificationCodes, issueAllCertificates, verifyCertificate, forceEligible } from "../api/certificates.api"
import { listEnrollments, listClassEnrollments, updateProgram } from "../api/programs.api"
import { getPublicCollection } from "../api/certBatch.api"
import toast from "react-hot-toast"

/* ============================================================
   PRD §FR-9: Certificate via Code Claim
   - Admin: see eligible participants, send 8-char verification codes
   - Participant: /claim page → enter email + code → download PDF
   - Public: /verify/:certNo verification result
   Exposes: CertificateScreen (overrides), ClaimScreen, AdminCertSendScreen
   ============================================================ */

// =============================================================
// PUBLIC CLAIM PAGE — /claim
// =============================================================
function ClaimScreen() {
  const orgName = useOrgName()
  const t = useT();
  const { lang, navigate } = useApp();
  const [email, setEmail] = React.useState("");
  const [code, setCode] = React.useState(["", "", "", "", "", "", "", ""]);
  const [state, setState] = React.useState("idle"); // idle | validating | success | error
  const [errorMsg, setErrorMsg] = React.useState("");
  const [result, setResult] = React.useState(null);
  const inputRefs = React.useRef([]);

  const onChangeChar = (i, v) => {
    const sanitized = (v || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 1);
    const next = [...code];
    next[i] = sanitized;
    setCode(next);
    if (sanitized && i < 7) inputRefs.current[i+1]?.focus();
  };
  const onKeyDown = (i, e) => {
    if (e.key === "Backspace" && !code[i] && i > 0) inputRefs.current[i-1]?.focus();
  };
  const onPaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData.getData("text") || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);
    const arr = pasted.split("").concat(Array(8).fill(""));
    setCode(arr.slice(0, 8));
    inputRefs.current[Math.min(pasted.length, 7)]?.focus();
  };

  const codeStr = code.join("");
  const ready = email.includes("@") && codeStr.length === 8;

  const submit = async () => {
    if (!ready) return;
    setState("validating");
    setErrorMsg("");
    try {
      const data = await claimCertificate(email, codeStr);
      setResult(data);
      setState("success");
      toast.success(lang === "id" ? "Sertifikat berhasil diklaim" : "Certificate claimed");
    } catch (err) {
      setErrorMsg(err?.response?.data?.error || err.message);
      setState("error");
    }
  };

  return (
    <div style={{minHeight:"100vh", background:"var(--bg)", display:"flex", flexDirection:"column"}}>
      {/* Public header */}
      <div style={{background:"#0B1417", color:"white", padding:"14px 32px"}} className="row between">
        <div className="row gap-3">
          <BrandLogo size={28} />
          <div>
            <div style={{fontWeight:600, fontSize:14}}>{orgName}</div>
            <div className="tiny" style={{color:"var(--g-40)"}}>{lang==="id"?"Klaim Sertifikat":"Certificate Claim"}</div>
          </div>
        </div>
        <div className="row gap-3">
          {/* --text-3 dirancang untuk teks di atas latar TERANG. Header ini
              gelap (--g-100), jadi label di sebelahnya sudah benar memakai
              --g-40 — tombol ini yang salah, hampir tak terlihat karena
              kontrasnya sangat rendah di atas latar gelap. */}
          <Btn variant="ghost" size="sm" icon={<Icon name="logout" size={12}/>} style={{color:"var(--g-40)"}} onClick={()=>navigate("login")}>
            {lang==="id"?"Kembali ke login":"Back to login"}
          </Btn>
        </div>
      </div>

      <div style={{flex:1, display:"grid", placeItems:"center", padding:"40px 24px"}}>
        <div style={{width:"100%", maxWidth: 540}}>
          {state !== "success" && (
            <>
              <div className="row gap-2 mb-3"><span className="tag outline mono">/ claim</span><span className="tag blue tiny">{lang==="id"?"Publik":"Public"}</span></div>
              <h1 className="h1" style={{fontSize: 32}}>{lang==="id"?"Klaim sertifikat Anda":"Claim your certificate"}</h1>
              <p className="muted mt-2">
                {lang==="id"
                  ? "Masukkan email dan kode verifikasi 8 karakter yang dikirim admin ke inbox Anda. Kode berlaku 7 hari."
                  : "Enter the email and 8-character verification code sent by admin to your inbox. The code is valid for 7 days."}
              </p>

              <div className="tile mt-5" style={{padding: 28}}>
                <div className="field mb-4">
                  <label>{t.email}</label>
                  <input type="email" value={email} onChange={e=>setEmail(e.target.value)}/>
                </div>

                <div className="field">
                  <label>{lang==="id"?"Kode verifikasi (8 karakter)":"Verification code (8 chars)"}</label>
                  <div className="row gap-1 mt-1" onPaste={onPaste} style={{ flexWrap: "wrap" }}>
                    {code.map((c, i) => (
                      <React.Fragment key={i}>
                        <input
                          ref={el => inputRefs.current[i] = el}
                          value={c}
                          onChange={e => onChangeChar(i, e.target.value)}
                          onKeyDown={e => onKeyDown(i, e)}
                          maxLength={1}
                          style={{
                            width: "clamp(32px, 9vw, 44px)",
                            height: "clamp(44px, 12vw, 56px)",
                            padding: 0,
                            textAlign: "center",
                            fontFamily: "var(--font-mono)", fontSize: 22, fontWeight: 500,
                            border: "1px solid", borderColor: c ? "var(--blue-60)" : "var(--border-strong)",
                            background: c ? "var(--blue-10)" : "var(--g-10)",
                            outline: "none",
                            textTransform: "uppercase",
                          }}
                        />
                        {i === 3 && <span className="mono" style={{color:"var(--text-tertiary)", padding:"0 4px"}}>—</span>}
                      </React.Fragment>
                    ))}
                  </div>
                  <div className="hint mono mt-2">{lang==="id"?"Contoh: K7M2-XQ9P":"Example: K7M2-XQ9P"}</div>
                </div>

                {state === "error" && (
                  <div className="banner mt-4" style={{background:"var(--red-10)", borderColor: "var(--red-60)"}}>
                    <Icon name="alert" color="var(--red-60)"/>
                    <div className="small" style={{flex:1, color:"var(--red-60)"}}>
                      {errorMsg || (lang==="id"?"Email atau kode salah, atau kode telah kadaluarsa.":"Email or code is incorrect, or the code has expired.")}
                    </div>
                  </div>
                )}

                <button className="btn lg mt-5" style={{width:"100%"}} onClick={submit} disabled={!ready || state === "validating"}>
                  {state === "validating"
                    ? (lang==="id"?"Memvalidasi…":"Validating…")
                    : (lang==="id"?"Klaim sertifikat":"Claim certificate")}
                  {state !== "validating" && <Icon name="arrow_right" size={16} className="btn-icon"/>}
                </button>

                <div className="divider"/>
                <div className="tiny muted" style={{lineHeight: 1.5}}>
                  {lang==="id"?"Kode tidak ditemukan atau kadaluarsa?":"Code missing or expired?"}{" "}
                  <a href="#">{lang==="id"?"Minta admin kirim ulang":"Ask admin to resend"}</a>.
                </div>
              </div>
            </>
          )}

          {state === "success" && (
            <ClaimSuccess lang={lang} email={email} navigate={navigate} result={result}/>
          )}
        </div>
      </div>

      <div style={{padding:"16px 32px", borderTop:"1px solid var(--border)", color:"var(--text-tertiary)"}} className="row between tiny">
        <span>© {new Date().getFullYear()} {orgName}</span>
        {orgName !== "Gladhy" && <PoweredByGladhy />}
      </div>
    </div>
  );
}

// Tanggal kelas disimpan sebagai tengah malam zona waktu browser admin; dibulatkan
// ke hari UTC terdekat supaya tidak mundur sehari (sama dengan cert.service.js).
function tanggalKelas(value) {
  if (!value) return null;
  return new Date(new Date(value).getTime() + 12 * 60 * 60 * 1000)
    .toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function CertificateVisualPreview({ template, profile, training, certNo, participantName, programName, level, issuedAt }) {
  const containerRef = React.useRef(null);
  const [width, setWidth] = React.useState(800);

  React.useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      if (!entries || !entries[0]) return;
      setWidth(entries[0].contentRect.width);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const scale = width / 800;

  if (!template) {
    return (
      <div
        ref={containerRef}
        style={{
          width: "100%",
          aspectRatio: "297/210",
          background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
          border: "1px solid var(--border)",
          borderRadius: "8px",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          padding: "20px",
          textAlign: "center"
        }}
      >
        <div style={{ fontSize: `${24 * scale}px`, fontWeight: "bold", color: "var(--text)" }}>{participantName}</div>
        <div style={{ fontSize: `${16 * scale}px`, color: "var(--text-secondary)", marginTop: "8px" }}>{programName}</div>
        <div style={{ fontSize: `${14 * scale}px`, color: "var(--text-tertiary)", marginTop: "12px", fontFamily: "monospace" }}>{certNo}</div>
      </div>
    );
  }

  const bgImageUrl = template.bgImageUrl;
  const canvasData = template.canvasData || {};
  const fields = canvasData.fields || {};
  const signatures = canvasData.signatures || [];
  const customTexts = canvasData.customTexts || [];
  const hasVisibleFields = Object.values(fields).some(f => f?.visible !== false);

  const dateFormatted = issuedAt
    ? new Date(issuedAt).toLocaleDateString('id-ID', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : '';

  const dobFormatted = profile?.dateOfBirth
    ? new Date(profile.dateOfBirth).toLocaleDateString('id-ID', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

  const placeDateOfBirth = [profile?.placeOfBirth, dobFormatted].filter(Boolean).join(', ') || '—';

  const validUntilDate = issuedAt
    ? (() => {
        const d = new Date(issuedAt);
        d.setFullYear(d.getFullYear() + 5);
        return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
      })()
    : '—';

  const textValues = {
    participantName: participantName || '—',
    certNo: certNo || '—',
    validUntil: validUntilDate,
    programName: programName || '—',
    oprcLevel: level ? `Level ${level}` : '—',
    issueDate: dateFormatted || '—',
    company: profile?.company || '—',
    nauticalCertNo: profile?.nauticalCertNo || '—',
    placeDateOfBirth: placeDateOfBirth,
    trainingStartDate: tanggalKelas(training?.startDate) || '—',
    trainingEndDate: tanggalKelas(training?.endDate) || '—',
    trainingLocation: training?.location || '—',
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        aspectRatio: "297/210",
        background: bgImageUrl ? `url(${bgImageUrl})` : "linear-gradient(135deg, var(--program-color, #B84E2A)11 0%, #fff 50%, var(--program-color, #B84E2A)22 100%)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        border: "1px solid var(--border)",
        borderRadius: "8px",
        boxShadow: "0 8px 30px rgba(0,0,0,0.085)",
        overflow: "hidden",
        userSelect: "none"
      }}
    >
      {/* Render Text Fields */}
      {Object.keys(fields).map((key) => {
        const field = fields[key];
        if (!field || field.visible === false) return null;

        if (key === 'qrCode') {
          const size = (field.size || 60) * scale;
          const left = field.left ?? 80;
          const top = field.top ?? 80;
          return (
            <div
              key={key}
              style={{
                position: "absolute",
                left: `${left}%`,
                top: `${top}%`,
                width: `${size}px`,
                height: `${size}px`,
                transform: "translate(-50%, -50%)",
                background: "white",
                padding: `${2 * scale}px`,
                border: "1px solid var(--border)",
                zIndex: 2,
              }}
            >
              <QRCodeBlock token={certNo} />
            </div>
          );
        }

        const value = textValues[key] || '—';
        const left = field.left ?? 50;
        const top = field.top ?? 50;
        const align = field.align || 'center';
        const fontSize = (field.fontSize || 14) * scale;
        const fontStyle = field.fontStyle || 'normal';
        const fontWeight = field.fontWeight || 'normal';
        const color = field.color || '#000000';

        let transform = 'translate(-50%, -50%)';
        if (align === 'left') transform = 'translate(0%, -50%)';
        if (align === 'right') transform = 'translate(-100%, -50%)';

        return (
          <div
            key={key}
            style={{
              position: "absolute",
              left: `${left}%`,
              top: `${top}%`,
              transform: transform,
              fontSize: `${fontSize}px`,
              fontFamily: "'Georgia', 'Arial', serif",
              fontStyle: fontStyle,
              fontWeight: fontWeight,
              color: color,
              textAlign: align,
              whiteSpace: "nowrap",
              zIndex: 2,
            }}
          >
            {value}
          </div>
        );
      })}

      {/* Render Custom Texts */}
      {customTexts.map((ct, idx) => {
        if (!ct || ct.visible === false) return null;
        const left = ct.left ?? 50;
        const top = ct.top ?? 50;
        const align = ct.align || 'center';
        const fontSize = (ct.fontSize || 16) * scale;
        let transform = 'translate(-50%, -50%)';
        if (align === 'left') transform = 'translate(0%, -50%)';
        if (align === 'right') transform = 'translate(-100%, -50%)';
        return (
          <div key={ct.id || idx} style={{
            position: "absolute", left: `${left}%`, top: `${top}%`,
            transform, fontSize: `${fontSize}px`,
            fontFamily: "'Georgia', 'Arial', serif",
            fontStyle: ct.fontStyle || 'normal', fontWeight: ct.fontWeight || 'normal',
            color: ct.color || '#000000', textAlign: align, whiteSpace: "nowrap", zIndex: 2,
          }}>
            {ct.text || ''}
          </div>
        );
      })}

      {/* Fallback: no canvas fields configured — render key info centered */}
      {!hasVisibleFields && (
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", zIndex: 2, padding: "0 8%" }}>
          <div style={{ fontSize: `${28 * scale}px`, fontFamily: "'Georgia', 'Arial', serif", fontWeight: "bold", fontStyle: "italic", color: "#0F172A", textAlign: "center" }}>{participantName}</div>
          <div style={{ fontSize: `${14 * scale}px`, fontFamily: "'Georgia', 'Arial', serif", color: "#475569", marginTop: `${8 * scale}px`, textAlign: "center" }}>{programName}</div>
          <div style={{ fontSize: `${11 * scale}px`, color: "#64748B", marginTop: `${6 * scale}px`, fontFamily: "monospace" }}>{certNo}</div>
        </div>
      )}

      {/* Render Signatures */}
      {signatures.map((sig, idx) => {
        if (!sig || sig.visible === false) return null;
        const left = sig.left ?? 50;
        const top = sig.top ?? 80;
        const name = sig.name || '';
        const title = sig.title || '';
        const imageUrl = sig.imageUrl;

        return (
          <div
            key={sig.id || idx}
            style={{
              position: "absolute",
              left: `${left}%`,
              top: `${top}%`,
              transform: "translate(-50%, -50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              zIndex: 2,
            }}
          >
            {imageUrl ? (
              <img src={imageUrl} alt="" style={{ maxHeight: `${30 * scale}px`, marginBottom: `${4 * scale}px`, display: "block" }} />
            ) : (
              <div style={{ height: `${30 * scale}px` }}></div>
            )}
            <div
              style={{
                fontSize: `${11 * scale}px`,
                fontFamily: "'Georgia', 'Arial', serif",
                fontWeight: "bold",
                textDecoration: "underline",
                color: "#000",
                whiteSpace: "nowrap"
              }}
            >
              {name}
            </div>
            <div
              style={{
                fontSize: `${9 * scale}px`,
                fontFamily: "'Georgia', 'Arial', serif",
                color: "#475569",
                marginTop: `${2 * scale}px`,
                whiteSpace: "nowrap"
              }}
            >
              {title}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function QRCodeBlock({ token }) {
  const url = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(token)}`;
  return <img src={url} alt="QR Code" style={{ width: "100%", height: "100%", display: "block" }} />;
}

function ClaimSuccess({ lang, email, navigate, result }) {
  const orgName = useOrgName()
  const certNo = result?.certNo || "—";
  const name = result?.participantName || "—";
  const program = result?.programName || "—";
  const pdfUrl = result?.pdfUrl;
  const level = result?.level;
  const issuedAt = result?.issuedAt;
  const template = result?.template;
  const profile = result?.profile;

  const issueDate = issuedAt ? new Date(issuedAt) : new Date();
  const issueYear = issueDate.getFullYear();
  const issueMonth = issueDate.getMonth() + 1;

  // Alamat kredensial permanen — halaman yang dirender server dan punya
  // pratinjau Open Graph, sehingga saat peserta menempelkannya di LinkedIn atau
  // WhatsApp yang muncul kartu bergambar sertifikatnya, bukan teks polos.
  // ?verify= lama tetap jadi cadangan untuk sertifikat yang terbit sebelum
  // kolom public_id ada.
  const verifyUrl = result?.publicId
    ? `${window.location.origin}/c/${result.publicId}`
    : `${window.location.origin}/?verify=${encodeURIComponent(certNo)}`;

  // certUrl LinkedIn menunjuk ke halaman kredensial, bukan ke PDF-nya.
  // pdfUrl adalah tautan bertanda tangan yang mati dalam hitungan menit — sekali
  // tertanam di profil LinkedIn seseorang, ia akan rusak di sana selamanya.
  const linkedInUrl = `https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=${encodeURIComponent(program)}&organizationName=${encodeURIComponent(orgName)}&issueYear=${issueYear}&issueMonth=${issueMonth}&certId=${encodeURIComponent(certNo)}&certUrl=${encodeURIComponent(verifyUrl)}`;
  const tweetText = lang === "id" 
    ? `Saya berhasil mendapatkan sertifikat resmi "${program}" dari ${orgName}!`
    : `I have successfully received my "${program}" certificate from ${orgName}!`;
  const tweetUrl = `https://x.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(verifyUrl)}`;

  return (
    <div>
      <div className="row gap-2 mb-3">
        <span className="tag green">
          <Icon name="check" size={10}/>
          {lang==="id"?"Sertifikat siap":"Certificate ready"}
        </span>
      </div>
      <h1 className="h1" style={{fontSize: 32}}>{lang==="id"?"Selamat — sertifikat berhasil dibuat.":"Congratulations — your certificate is ready."}</h1>
      <p className="muted mt-2">
        {lang==="id"
          ? "Sertifikat digital Anda telah diterbitkan secara resmi dengan data terverifikasi."
          : "Your digital certificate has been officially issued with verified data."}
      </p>

      <div className="tile mt-4" style={{padding: 0, overflow: "hidden"}}>
        {/* Visual Preview */}
        <div style={{padding: 20, borderBottom: "1px solid var(--border)", background: "var(--g-10)"}}>
          <div className="small muted mb-3" style={{fontWeight: 500}}>{lang==="id"?"Preview Desain Asli:":"Original Design Preview:"}</div>
          <CertificateVisualPreview 
            template={template}
            profile={profile}
            training={result?.training}
            certNo={certNo}
            participantName={name}
            programName={program}
            level={level}
            issuedAt={issuedAt}
          />
        </div>

        {/* Certificate Details */}
        <div style={{padding: "20px 24px", borderBottom: "1px solid var(--border)"}}>
          <div className="row gap-3">
            <div style={{width:48, height:48, background:"var(--green-10)", display:"grid", placeItems:"center"}}>
              <Icon name="cert" size={22} color="var(--green-60)"/>
            </div>
            <div style={{flex:1}}>
              <div className="small muted">{lang==="id"?"Nomor sertifikat":"Certificate number"}</div>
              <div className="mono" style={{fontSize: 18, fontWeight: 500}}>{certNo}</div>
            </div>
            <span className="tag green"><Icon name="check" size={10}/>{lang==="id"?"VALID":"VALID"}</span>
          </div>
        </div>

        <TableWrap>
          <table className="table table-compact">
            <tbody>
              <tr><td className="muted small">{lang==="id"?"Nama":"Name"}</td><td className="small" style={{fontWeight: 500}}>{name}</td></tr>
              <tr><td className="muted small">{lang==="id"?"Program":"Program"}</td><td className="small">{program}</td></tr>
              {level && <tr><td className="muted small">{lang==="id"?"Tingkat":"Level"}</td><td className="small">Level {level}</td></tr>}
              {profile?.company && <tr><td className="muted small">{lang==="id"?"Perusahaan":"Company"}</td><td className="small">{profile.company}</td></tr>}
              {issuedAt && <tr><td className="muted small">{lang==="id"?"Diterbitkan":"Issued on"}</td><td className="small mono">{new Date(issuedAt).toLocaleDateString(lang==="id"?"id-ID":"en-US", {day:'numeric', month:'long', year:'numeric'})}</td></tr>}
            </tbody>
          </table>
        </TableWrap>

        {/* Share Section */}
        <div style={{padding: "16px 24px", borderTop: "1px solid var(--border)", display: "flex", flexDirection: "column", gap: 12}}>
          <div className="small" style={{fontWeight: 600, color: "var(--text)"}}>
            {lang === "id" ? "Bagikan Pencapaian Anda:" : "Share Your Achievement:"}
          </div>
          <div className="row gap-2" style={{width: "100%"}}>
            <a
              href={linkedInUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                flex: 1,
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 16px",
                fontSize: 13,
                fontWeight: 600,
                background: "#0077B5",
                color: "white",
                borderRadius: "var(--r-md)",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0, 119, 181, 0.15)",
                transition: "transform 0.1s ease"
              }}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <svg viewBox="0 0 24 24" width={14} height={14} fill="currentColor">
                <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.779-1.75-1.75s.784-1.75 1.75-1.75 1.75.779 1.75 1.75-.784 1.75-1.75 1.75zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
              </svg>
              {lang === "id" ? "Tambah ke LinkedIn" : "Add to LinkedIn"}
            </a>

            <a
              href={tweetUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                flex: 1,
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 16px",
                fontSize: 13,
                fontWeight: 600,
                background: "#000000",
                color: "white",
                borderRadius: "var(--r-md)",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                transition: "transform 0.1s ease"
              }}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <svg viewBox="0 0 24 24" width={12} height={12} fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              {lang === "id" ? "Bagikan di X" : "Share on X"}
            </a>
          </div>
        </div>

        {/* Download Section */}
        <div className="row gap-2" style={{padding: 16, borderTop:"1px solid var(--border)", background: "var(--g-10)"}}>
          {pdfUrl ? (
            <a className="btn-primary" style={{flex: 1, textDecoration:"none", display:"inline-flex", alignItems:"center", gap:6, padding:"7px 12px", fontSize:13, fontWeight:500, background:"var(--program-color)", color:"white", borderRadius:"var(--r-md)", border:"1px solid transparent", justifyContent:"center"}} href={pdfUrl} target="_blank" rel="noopener noreferrer">
              <Icon name="download" size={14}/>{lang==="id"?"Download PDF":"Download PDF"}
            </a>
          ) : (
            <Btn variant="primary" style={{flex: 1}} disabled icon={<Icon name="download" size={14}/>}>
              {lang==="id"?"PDF sedang disiapkan":"PDF being prepared"}
            </Btn>
          )}
          <Btn variant="secondary" icon={<Icon name="copy" size={14}/>} onClick={()=>{
            // Sengaja alamat kredensial, bukan pdfUrl: tautan PDF ditandatangani
            // dan kedaluwarsa, jadi tautan yang disalin peserta akan mati tak
            // lama setelah ia membagikannya.
            navigator.clipboard?.writeText(verifyUrl);
            toast.success(lang==="id"?"Tautan disalin":"Link copied");
          }}>
            {lang==="id"?"Salin tautan":"Copy link"}
          </Btn>
        </div>

        {/* Alamat kredensial permanen */}
        <div style={{padding:"14px 24px", borderTop:"1px solid var(--border)"}}>
          <div className="small muted" style={{marginBottom:6}}>
            {lang==="id"?"Alamat kredensial Anda — bisa dibagikan ke siapa pun:":"Your credential address — shareable with anyone:"}
          </div>
          <a className="mono small" href={verifyUrl} target="_blank" rel="noopener noreferrer"
             style={{wordBreak:"break-all", color:"var(--program-color)"}}>
            {verifyUrl}
          </a>
        </div>
      </div>

      <p className="tiny muted mt-4">
        {lang==="id"
          ? <>Salinan dikirim ke <span className="mono">{email}</span>. Sertifikat sudah dapat dilihat di akun Anda.</>
          : <>A copy has been sent to <span className="mono">{email}</span>. The certificate is also visible in your account.</>}
      </p>
      <Btn variant="ghost" className="mt-3" icon={<Icon name="arrow_right" size={14}/>} onClick={()=>{ navigate("login"); }}>
        {lang==="id"?`Masuk ke akun ${orgName}`:`Sign in to ${orgName} account`}
      </Btn>
    </div>
  );
}

// =============================================================
// ADMIN — SEND VERIFICATION CODE
// =============================================================
function AdminCertSendScreen() {
  const orgName = useOrgName()
  const t = useT();
  const { lang, navigate, currentProgramId, currentProgram, currentClassId, currentClass, user, refreshPrograms } = useApp();
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const [enrollments, setEnrollments] = React.useState([]);
  const [selected, setSelected] = React.useState({});
  const [filter, setFilter] = React.useState("eligible");
  const [sentDialog, setSentDialog] = React.useState({ open: false, count: 0 });
  const [sending, setSending] = React.useState(false);
  const [savingAutoSend, setSavingAutoSend] = React.useState(false);
  const [issueAllConfirm, setIssueAllConfirm] = React.useState(false); // FR-24

  // Dengan kelas terpilih, batch-nya hanya peserta angkatan itu — sama seperti
  // Reports dan Users. Sebelum ini rute selalu memuat SELURUH program terlepas
  // dari kelas yang sedang aktif di sidebar, jadi admin yang mengira sedang
  // melihat/mengirim untuk satu kelas ("test-1") sebenarnya melihat 22 peserta
  // lintas kelas sekaligus.
  const refresh = React.useCallback(() => {
    if (!currentProgramId) return;
    const ambil = currentClassId
      ? listClassEnrollments(currentProgramId, currentClassId)
      : listEnrollments(currentProgramId);
    ambil
      .then(setEnrollments)
      .catch((err) => toast.error(err?.response?.data?.error || err.message));
  }, [currentProgramId, currentClassId]);

  React.useEffect(() => { refresh(); }, [refresh]);

  function rowStatus(e) {
    if (e.certificate?.claimed_at) return "claimed";
    if (e.certificate?.verification_code) return "code_sent";
    if (e.cert_eligible) return "eligible";
    return "not_eligible";
  }

  function rowReason(e) {
    if ((e.attendance_pct ?? 0) < 80) return lang === "id" ? "Hadir < 80%" : "Attendance < 80%";
    if (e.posttest_score == null) return lang === "id" ? "Posttest belum" : "Posttest pending";
    if (e.posttest_score < 70) return "Posttest < 70";
    return "—";
  }

  function initials(name) {
    return name ? name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase() : "??";
  }

  const rows = enrollments.map((e) => ({
    id: e.id,
    i: initials(e.user.full_name),
    n: e.user.full_name,
    email: e.user.email,
    course: currentProgram?.code?.toUpperCase() || "—",
    att: Math.round(e.attendance_pct ?? 0),
    pretest: e.pretest_score != null ? Math.round(e.pretest_score) : null,
    posttest: e.posttest_score != null ? Math.round(e.posttest_score) : null,
    status: rowStatus(e),
    reason: rowReason(e),
    sent: e.certificate?.code_expires_at ? new Date(e.certificate.code_expires_at).toLocaleDateString(lang === "id" ? "id-ID" : "en-US") : null,
    pdfUrl: e.certificate?.pdf_url,
  }));

  const filtered = filter === "all" ? rows : rows.filter(r => r.status === filter);
  const eligibleCount = rows.filter(r => r.status === "eligible").length;
  const codeSentCount = rows.filter(r => r.status === "code_sent").length;
  const claimedCount = rows.filter(r => r.status === "claimed").length;
  const selCount = Object.keys(selected).filter(k => selected[k]).length;

  const toggleAll = () => {
    const selectableIds = filtered.filter(r => r.status === "eligible").map(r => r.id);
    const allSelected = selectableIds.length > 0 && selectableIds.every(id => selected[id]);
    const next = {...selected};
    selectableIds.forEach(id => { next[id] = !allSelected; });
    setSelected(next);
  };

  async function handleSendCodes(ids) {
    if (!ids?.length) return;
    setSending(true);
    try {
      const { results } = await sendVerificationCodes(currentProgramId, ids);
      const ok = results.filter((r) => r.ok).length;
      const fail = results.length - ok;
      setSentDialog({ open: true, count: ok });
      if (fail > 0) toast.error(`${fail} ${lang === "id" ? "gagal terkirim" : "failed to send"}`);
      setSelected({});
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message);
    } finally {
      setSending(false);
    }
  }

  async function handleForceEligible(enrollmentId) {
    setSending(true);
    try {
      await forceEligible(currentProgramId, enrollmentId);
      toast.success(lang === "id" ? "Peserta berhasil dijadikan eligible" : "Participant marked as eligible");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message);
    } finally {
      setSending(false);
    }
  }

  // Sakelar per program: kirim kode klaim otomatis begitu peserta memenuhi
  // syarat, tanpa perlu Send Code/Issue All manual. TIDAK retroaktif —
  // hanya berlaku untuk peserta yang baru memenuhi syarat setelah dinyalakan;
  // yang sudah eligible lebih dulu tetap lewat tombol manual di bawah.
  async function handleToggleAutoSend(nilaiBaru) {
    setSavingAutoSend(true);
    try {
      await updateProgram(currentProgramId, { auto_send_cert_code: nilaiBaru });
      await refreshPrograms();
      toast.success(nilaiBaru
        ? (lang === "id" ? "Kode akan terkirim otomatis mulai sekarang" : "Codes will now be sent automatically")
        : (lang === "id" ? "Kembali ke pengiriman manual" : "Back to manual sending"));
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message);
    } finally {
      setSavingAutoSend(false);
    }
  }

  // FR-24: Bulk issue-all — send codes to ALL eligible (not yet sent) in one shot
  async function handleIssueAll() {
    setIssueAllConfirm(false);
    setSending(true);
    try {
      const data = await issueAllCertificates(currentProgramId, currentClassId);
      setSentDialog({ open: true, count: data.sent });
      if (data.errors?.length > 0) {
        toast.error(`${data.errors.length} ${lang === "id" ? "gagal terkirim" : "failed to send"}`);
      }
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="main-inner">
      <div className="page-header">
        <div className="page-header-text">
          <div className="eyebrow">{orgName} · {lang === "id" ? "Sertifikat" : "Certificates"}</div>
          <h1 className="h1" style={{ marginTop: 6 }}>
            {lang==="id"?"Kirim kode verifikasi sertifikat":"Send certificate verification codes"}
          </h1>
          <div className="muted mt-1">
            {lang==="id"
              ? "Peserta yang memenuhi syarat (hadir ≥ 80%, watch ≥ 90%, posttest ≥ 70) dapat menerima kode 8-karakter via email."
              : "Eligible participants (attendance ≥ 80%, watch ≥ 90%, posttest ≥ 70) receive an 8-character code by email."}
          </div>
        </div>
        <div className="page-header-actions">
          <Btn variant="ghost" size="sm" icon={<Icon name="refresh" size={14}/>} onClick={refresh}>
            Refresh
          </Btn>
          {/* FR-24: Bulk issue-all button — only show if there are unsent eligible */}
          {eligibleCount > 0 && (
            <Btn variant="secondary" size="sm" icon={<Icon name="send" size={14}/>}
              disabled={sending}
              onClick={() => setIssueAllConfirm(true)}>
              {lang === "id" ? `Kirim semua (${eligibleCount})` : `Send all (${eligibleCount})`}
            </Btn>
          )}
          <Btn variant="primary" icon={<Icon name="send" size={14}/>}
            disabled={selCount === 0 || sending}
            onClick={() => handleSendCodes(Object.keys(selected).filter((k) => selected[k]))}>
            {sending
              ? (lang === "id" ? "Mengirim…" : "Sending…")
              : (lang === "id" ? `Kirim kode (${selCount})` : `Send codes (${selCount})`)}
          </Btn>
        </div>
      </div>

      <div className="small muted mb-3">
        {lang === "id" ? "Cakupan batch: " : "Batch scope: "}
        <strong style={{ color: "var(--text)" }}>
          {currentClass?.name
            ? `${lang === "id" ? "Kelas" : "Class"} ${currentClass.name}`
            : (lang === "id" ? "Semua kelas (seluruh program)" : "All classes (whole program)")}
        </strong>
        {" · "}
        {lang === "id" ? "ubah lewat pemilih Kelas di sidebar." : "change it from the Class picker in the sidebar."}
      </div>

      <div className="grid grid-4 mb-4">
        <Stat label={lang==="id"?"Total batch":"Total batch"} value={String(rows.length)}/>
        <Stat label={lang==="id"?"Memenuhi syarat":"Eligible"} value={String(eligibleCount)} hint={lang==="id"?"belum dikirim kode":"not yet sent"}/>
        <Stat label={lang==="id"?"Kode terkirim":"Codes sent"} value={String(codeSentCount)} hint={lang==="id"?"menunggu klaim":"awaiting claim"}/>
        <Stat label={lang==="id"?"Sertifikat diklaim":"Certificates claimed"} value={String(claimedCount)}/>
      </div>

      <div className="tile mb-4">
        <label className="row gap-3" style={{ alignItems: "flex-start", padding: "14px 16px", cursor: savingAutoSend ? "default" : "pointer" }}>
          <input
            type="checkbox"
            checked={!!currentProgram?.auto_send_cert_code}
            disabled={savingAutoSend}
            onChange={(e) => handleToggleAutoSend(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span className="col gap-1">
            <span className="small" style={{ fontWeight: 600 }}>
              {lang==="id" ? "Kirim kode otomatis" : "Send codes automatically"}
            </span>
            <span className="muted tiny">
              {lang==="id"
                ? "Begitu peserta memenuhi syarat, kode langsung terkirim tanpa perlu diklik. Tidak berlaku surut — peserta yang sudah eligible sebelumnya tetap lewat tombol di bawah."
                : "The moment a participant becomes eligible, their code is sent without a click. Not retroactive — participants already eligible still go through the buttons below."}
            </span>
          </span>
        </label>
      </div>

      <div className="tile">
        <div className="tile-header">
          <div className="row gap-1">
            {[
              {id:"eligible",     l: lang==="id"?"Memenuhi syarat":"Eligible",         n: eligibleCount},
              {id:"code_sent",    l: lang==="id"?"Kode terkirim":"Code sent",          n: codeSentCount},
              {id:"claimed",      l: lang==="id"?"Sudah diklaim":"Claimed",            n: claimedCount},
              {id:"not_eligible", l: lang==="id"?"Belum memenuhi":"Not eligible",      n: rows.filter(r => r.status === "not_eligible").length},
              {id:"all",          l: lang==="id"?"Semua":"All",                        n: rows.length},
            ].map(f => (
              <button key={f.id} onClick={()=>setFilter(f.id)}
                style={{display:"inline-flex", alignItems:"center", gap:4, background:"transparent", border:"none", borderBottom: filter===f.id?"2px solid var(--program-color)":"2px solid transparent", padding:"6px 12px", minWidth:0, height:"auto", borderRadius:0, fontWeight: filter===f.id?500:400, cursor:"pointer", fontSize:13, fontFamily:"inherit", color: filter===f.id ? "var(--text)" : "var(--text-3)"}}>
                {f.l} <span className="mono tiny muted">({f.n})</span>
              </button>
            ))}
          </div>
          {selCount > 0 && (
            <span className="tag blue"><span className="dot"/>{selCount} {lang==="id"?"dipilih":"selected"}</span>
          )}
        </div>
        <TableWrap>
        <table className="table table-compact">
          <thead><tr>
            <th style={{width: 32}}>
              <input type="checkbox" onChange={toggleAll}
                checked={filtered.filter(r=>r.status==="eligible").length>0 && filtered.filter(r=>r.status==="eligible").every(r=>selected[r.id])}
              />
            </th>
            <th>{lang==="id"?"Peserta":"Participant"}</th>
            <th>{lang==="id"?"Program":"Program"}</th>
            <th>{lang==="id"?"Hadir":"Attend."}</th>
            <th>Pretest</th>
            <th>Posttest</th>
            <th>{lang==="id"?"Status":"Status"}</th>
            <th></th>
          </tr></thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="muted small" style={{textAlign:"center", padding:20}}>
                {lang==="id"?"Tidak ada peserta di kategori ini.":"No participants in this category."}
              </td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.id}>
                <td>
                  {r.status === "eligible" && (
                    <input type="checkbox" checked={!!selected[r.id]} onChange={()=>setSelected({...selected, [r.id]: !selected[r.id]})}/>
                  )}
                </td>
                <td>
                  <div className="row gap-2">
                    <div className="avatar" style={{width:26, height:26, fontSize:10}}>{r.i}</div>
                    <div>
                      <span className="small" style={{fontWeight: 500}}>{r.n}</span>
                      <div className="tiny muted">{r.email}</div>
                    </div>
                  </div>
                </td>
                <td className="tiny muted mono">{r.course}</td>
                <td><span className="mono small tnum" style={{color: r.att < 80 ? "var(--red-60)" : "var(--text)"}}>{r.att}%</span></td>
                <td>{r.pretest === null ? <span className="tag outline tiny">—</span> : <span className="mono small tnum">{r.pretest}</span>}</td>
                <td>
                  {r.posttest === null
                    ? <span className="tag outline tiny">—</span>
                    : <span className="mono small tnum" style={{color: r.posttest < 70 ? "var(--red-60)" : r.posttest >= 85 ? "var(--green-60)" : "var(--text)"}}>{r.posttest}</span>
                  }
                </td>
                <td>
                  {r.status === "eligible"    && <span className="tag green tiny"><span className="dot"/>{lang==="id"?"Memenuhi syarat":"Eligible"}</span>}
                  {r.status === "code_sent"   && <span className="tag blue tiny"><Icon name="send" size={10}/>{lang==="id"?"Kode terkirim":"Code sent"}</span>}
                  {r.status === "claimed"     && <span className="tag tiny" style={{background:"var(--g-100)", color:"var(--bg)"}}><Icon name="check" size={10}/>{lang==="id"?"Diklaim":"Claimed"}</span>}
                  {r.status === "not_eligible"&& <span className="tag outline tiny" title={r.reason}>{lang==="id"?"Belum":"Not yet"}</span>}
                </td>
                <td style={{textAlign: "right"}}>
                  {r.status === "eligible" && (
                    <button className="btn sm secondary" disabled={sending} onClick={() => handleSendCodes([r.id])}>
                      <Icon name="send" size={11}/>{lang==="id"?"Kirim":"Send"}
                    </button>
                  )}
                  {r.status === "code_sent" && (
                    <div className="row gap-1" style={{justifyContent:"flex-end"}}>
                      <span className="tiny mono muted">{r.sent}</span>
                      <button className="btn sm ghost icon-only" title={lang==="id"?"Kirim ulang":"Resend"} onClick={() => handleSendCodes([r.id])}>
                        <Icon name="refresh" size={12}/>
                      </button>
                    </div>
                  )}
                  {r.status === "claimed" && r.pdfUrl && (
                    <a className="btn sm ghost" href={r.pdfUrl} target="_blank" rel="noopener noreferrer">
                      <Icon name="download" size={12}/>PDF
                    </a>
                  )}
                  {r.status === "not_eligible" && (
                    isSuperAdmin ? (
                      <button className="btn sm ghost" disabled={sending} title={r.reason} onClick={() => handleForceEligible(r.id)}>
                        <Icon name="check" size={11}/>{lang === "id" ? "Override Eligible" : "Override Eligible"}
                      </button>
                    ) : (
                      <span className="tiny muted">{r.reason}</span>
                    )
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </TableWrap>
      </div>

      {sentDialog.open && (
        <SentDialog count={sentDialog.count} lang={lang} onClose={() => setSentDialog({ open: false, count: 0 })}/>
      )}

      {/* FR-24: Confirm bulk issue-all */}
      {issueAllConfirm && (
        <div className="modal-overlay" onClick={() => setIssueAllConfirm(false)}>
          <div className="tile" style={{ maxWidth: 420, padding: "28px 28px 20px" }} onClick={e => e.stopPropagation()}>
            <div className="row gap-3 mb-4" style={{ alignItems: "flex-start" }}>
              <div style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--surface-2)", display: "grid", placeItems: "center", flexShrink: 0 }}>
                <Icon name="send" size={18} color="var(--program-color)" />
              </div>
              <div>
                <div className="h3">{lang === "id" ? "Kirim semua kode?" : "Send all codes?"}</div>
                <p className="muted small mt-2">
                  {lang === "id"
                    ? `Kode verifikasi akan dikirim ke ${eligibleCount} peserta yang memenuhi syarat dan belum menerima kode. Proses ini tidak bisa dibatalkan.`
                    : `Verification codes will be sent to ${eligibleCount} eligible participants who haven't received one yet. This cannot be undone.`}
                </p>
              </div>
            </div>
            <div className="row gap-2" style={{ justifyContent: "flex-end" }}>
              <Btn variant="ghost" onClick={() => setIssueAllConfirm(false)}>
                {lang === "id" ? "Batal" : "Cancel"}
              </Btn>
              <Btn variant="primary" icon={<Icon name="send" size={14} />} onClick={handleIssueAll}>
                {lang === "id" ? `Kirim ke ${eligibleCount} peserta` : `Send to ${eligibleCount} participants`}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SentDialog({ count, lang, onClose }) {
  const orgName = useOrgName()
  return (
    <div className="modal-overlay" style={{position:"fixed", inset:0, background:"rgba(0,0,0,.5)", display:"grid", placeItems:"center", zIndex: 100}}>
      <div className="tile" style={{width: 480, maxWidth: "90vw", padding: 0, background: "var(--surface)"}}>
        <div style={{padding: 24, textAlign: "center"}}>
          <div style={{width: 64, height: 64, borderRadius:"50%", background:"var(--green-10)", display:"inline-grid", placeItems:"center", marginBottom: 12}}>
            <Icon name="send" size={28} color="var(--green-60)"/>
          </div>
          <h2 className="h2" style={{fontSize: 20}}>{lang==="id"?`Kode terkirim ke ${count} peserta`:`Codes sent to ${count} participant${count>1?"s":""}`}</h2>
          <p className="muted small mt-2">
            {lang==="id"
              ? "Setiap peserta menerima email berisi kode 8-karakter dan instruksi klaim di /claim. Kode kadaluarsa dalam 7 hari."
              : "Each participant got an email with their 8-char code and /claim instructions. Codes expire in 7 days."}
          </p>
          <div className="tile mt-4" style={{padding: "12px 16px", background:"var(--g-10)", textAlign: "left"}}>
            <div className="tiny muted mb-1">{lang==="id"?"Contoh email yang dikirim":"Sample of the email sent"}</div>
            <div className="small" style={{lineHeight: 1.5}}>
              <strong>Subject:</strong> {lang==="id"?`Klaim sertifikat Anda · ${orgName}`:`Claim your certificate · ${orgName}`}<br/>
              <span className="muted">
                {lang==="id"?"Halo, kode klaim Anda:":"Hello, your claim code:"}{" "}
                <span className="mono" style={{padding:"2px 6px", background:"var(--blue-10)", color:"var(--blue-80)", fontWeight:500}}>K7M2-XQ9P</span>
              </span>
            </div>
          </div>
        </div>
        <div className="row gap-2" style={{padding: "12px 16px", borderTop: "1px solid var(--border)", background:"var(--g-10)"}}>
          <Btn variant="ghost" size="sm" style={{flex: 1}}>{lang==="id"?"Salin daftar kode":"Copy code list"}</Btn>
          <Btn variant="primary" size="sm" style={{flex: 1}} onClick={onClose}>{lang==="id"?"Tutup":"Done"}</Btn>
        </div>
      </div>
    </div>
  );
}

function VerifyPublicScreen() {
  const orgName = useOrgName()
  const t = useT();
  const { lang, navigate, pageState } = useApp();
  const certNoParam = pageState?.certNo || "";

  const [inputCertNo, setInputCertNo] = React.useState(certNoParam);
  const [verifying, setVerifying] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [errorMsg, setErrorMsg] = React.useState("");

  const runVerification = React.useCallback(async (num) => {
    if (!num.trim()) return;
    setVerifying(true);
    setErrorMsg("");
    setResult(null);
    try {
      const data = await verifyCertificate(num.trim());
      setResult(data);
    } catch (err) {
      setErrorMsg(err?.response?.data?.error || err.message || "Certificate verification failed");
    } finally {
      setVerifying(false);
    }
  }, []);

  React.useEffect(() => {
    if (certNoParam) {
      setInputCertNo(certNoParam);
      runVerification(certNoParam);
    }
  }, [certNoParam, runVerification]);

  const handleSubmit = (e) => {
    e.preventDefault();
    runVerification(inputCertNo);
  };

  const issueDate = result?.issuedAt ? new Date(result.issuedAt) : null;
  const issueYear = issueDate?.getFullYear();
  const issueMonth = issueDate ? issueDate.getMonth() + 1 : null;
  // Alamat kredensial bila barisnya sudah punya; ?verify= lama tetap jadi
  // cadangan, termasuk sebelum hasil verifikasi datang.
  const verifyUrl = result?.publicId
    ? `${window.location.origin}/c/${result.publicId}`
    : `${window.location.origin}/?verify=${encodeURIComponent(result?.certNo || inputCertNo)}`;
  
  const linkedInUrl = result ? `https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=${encodeURIComponent(result.programName)}&organizationName=${encodeURIComponent(orgName)}&issueYear=${issueYear}&issueMonth=${issueMonth}&certId=${encodeURIComponent(result.certNo)}&certUrl=${encodeURIComponent(verifyUrl)}` : '#';
  const tweetText = result ? (lang === "id" 
    ? `Verifikasi sertifikat "${result.programName}" untuk ${result.participantName} di ${orgName}.`
    : `Verify certificate "${result.programName}" for ${result.participantName} at ${orgName}.`) : '';
  const tweetUrl = result ? `https://x.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(verifyUrl)}` : '#';

  return (
    <div style={{minHeight:"100vh", background:"var(--bg)", display:"flex", flexDirection:"column"}}>
      {/* Public header */}
      <div style={{background:"#0B1417", color:"white", padding:"14px 32px"}} className="row between">
        <div className="row gap-3">
          <BrandLogo size={28} />
          <div>
            <div style={{fontWeight:600, fontSize:14}}>{orgName}</div>
            <div className="tiny" style={{color:"var(--g-40)"}}>{lang==="id"?"Verifikasi Sertifikat":"Certificate Verification"}</div>
          </div>
        </div>
        <div className="row gap-3">
          {/* --text-3 dirancang untuk teks di atas latar TERANG. Header ini
              gelap (--g-100), jadi label di sebelahnya sudah benar memakai
              --g-40 — tombol ini yang salah, hampir tak terlihat karena
              kontrasnya sangat rendah di atas latar gelap. */}
          <Btn variant="ghost" size="sm" icon={<Icon name="logout" size={12}/>} style={{color:"var(--g-40)"}} onClick={()=>navigate("login")}>
            {lang==="id"?"Kembali ke login":"Back to login"}
          </Btn>
        </div>
      </div>

      <div style={{flex:1, display:"grid", placeItems:"center", padding:"40px 24px"}}>
        <div style={{width:"100%", maxWidth: 640}}>
          
          <div className="row gap-2 mb-3">
            <span className="tag outline mono">/ verify</span>
            <span className="tag blue tiny">{lang==="id"?"Publik":"Public"}</span>
          </div>
          
          <h1 className="h1 mb-2" style={{fontSize: 32}}>
            {lang==="id"?"Verifikasi Keaslian Sertifikat":"Verify Certificate Authenticity"}
          </h1>
          <p className="muted">
            {lang==="id"
              ? "Masukkan nomor sertifikat resmi untuk melihat status validitas dan desain sertifikat asli."
              : "Enter an official certificate number to view its validity status and original certificate design."}
          </p>

          <form onSubmit={handleSubmit} className="tile mt-4 mb-4" style={{padding: 20}}>
            <div className="field">
              <label>{lang==="id"?"Nomor Sertifikat":"Certificate Number"}</label>
              <div className="row gap-2 mt-1">
                <input 
                  type="text" 
                  value={inputCertNo} 
                  onChange={e=>setInputCertNo(e.target.value)} 
                  placeholder="mis. LEMBAGA/PROGRAM/2026/0507A2"
                  style={{flex: 1}}
                  required
                />
                <button type="submit" className="btn primary" disabled={verifying || !inputCertNo.trim()} style={{height: "auto", minHeight: 38}}>
                  {verifying ? (lang==="id"?"Memverifikasi…":"Verifying…") : (lang==="id"?"Verifikasi":"Verify")}
                </button>
              </div>
            </div>
          </form>

          {verifying && (
            <div className="tile tile-pad" style={{textAlign:"center", padding:40}}>
              <p className="small muted">{lang==="id"?"Sedang memproses verifikasi...":"Processing verification..."}</p>
            </div>
          )}

          {errorMsg && (
            <div className="banner mb-4" style={{background:"var(--red-10)", borderColor: "var(--red-60)"}}>
              <Icon name="alert" color="var(--red-60)"/>
              <div className="small" style={{flex:1, color:"var(--red-60)"}}>
                <strong>{lang==="id"?"Sertifikat Tidak Valid atau Belum Diklaim":"Certificate Invalid or Not Yet Claimed"}</strong>. {errorMsg}
              </div>
            </div>
          )}

          {result?.valid && (
            <div className="tile animate-fade-in" style={{padding: 0, overflow: "hidden"}}>
              {/* Certificate preview */}
              <div style={{padding: 24, borderBottom: "1px solid var(--border)", background: "var(--g-10)"}}>
                <div className="small muted mb-3" style={{fontWeight: 500}}>{lang==="id"?"Preview Desain Asli:":"Original Design Preview:"}</div>
                <CertificateVisualPreview 
                  template={result.template}
                  profile={result.profile}
                  training={result.training}
                  certNo={result.certNo}
                  participantName={result.participantName}
                  programName={result.programName}
                  level={result.level}
                  issuedAt={result.issuedAt}
                />
              </div>

              {/* Verified details */}
              <div style={{padding: "20px 24px", borderBottom: "1px solid var(--border)"}}>
                <div className="row gap-3 between mb-4">
                  <div style={{flex:1}}>
                    <div className="small muted">{lang==="id"?"Nomor sertifikat":"Certificate number"}</div>
                    <div className="mono" style={{fontSize: 18, fontWeight: 500}}>{result.certNo}</div>
                  </div>
                  <span className="tag green" style={{fontSize: 12, padding: "4px 8px"}}><Icon name="check" size={12}/>{lang==="id"?"TERVERIFIKASI / VALID":"VERIFIED / VALID"}</span>
                </div>

                <TableWrap>
                  <table className="table table-compact">
                    <tbody>
                      <tr>
                        <td className="muted small" style={{width: 160}}>{lang==="id"?"Nama Lengkap":"Participant Name"}</td>
                        <td className="small" style={{fontWeight: 500}}>{result.participantName}</td>
                      </tr>
                      <tr>
                        <td className="muted small">{lang==="id"?"Program Pelatihan":"Training Program"}</td>
                        <td className="small">{result.programName}</td>
                      </tr>
                      {result.level != null && (
                        <tr>
                          <td className="muted small">Level</td>
                          <td className="small">Level {result.level}</td>
                        </tr>
                      )}
                      {result.profile?.company && (
                        <tr>
                          <td className="muted small">{lang==="id"?"Perusahaan / Instansi":"Company / Agency"}</td>
                          <td className="small">{result.profile.company}</td>
                        </tr>
                      )}
                      {result.profile?.nauticalCertNo && result.profile?.nauticalCertNo !== '—' && (
                        <tr>
                          <td className="muted small">{lang==="id"?"Sertifikat Nautical":"Nautical Cert No"}</td>
                          <td className="small mono">{result.profile.nauticalCertNo}</td>
                        </tr>
                      )}
                      {result.profile?.hublaCertNo && result.profile?.hublaCertNo !== '—' && (
                        <tr>
                          <td className="muted small">{lang==="id"?"Sertifikat Hubla (DGST)":"Hubla Cert No (DGST)"}</td>
                          <td className="small mono">{result.profile.hublaCertNo}</td>
                        </tr>
                      )}
                      {result.profile?.placeOfBirth && (
                        <tr>
                          <td className="muted small">{lang==="id"?"Tempat & Tanggal Lahir":"Place & Date of Birth"}</td>
                          <td className="small">
                            {[
                              result.profile.placeOfBirth, 
                              result.profile.dateOfBirth ? new Date(result.profile.dateOfBirth).toLocaleDateString('id-ID', {day:'numeric', month:'long', year:'numeric'}) : null
                            ].filter(Boolean).join(', ')}
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td className="muted small">{lang==="id"?"Diterbitkan":"Issued on"}</td>
                        <td className="small mono">{new Date(result.issuedAt).toLocaleDateString(lang==="id"?"id-ID":"en-US", {day:'numeric', month:'long', year:'numeric'})}</td>
                      </tr>
                    </tbody>
                  </table>
                </TableWrap>
              </div>

              {/* Share section */}
              <div style={{padding: "16px 24px", borderBottom: "1px solid var(--border)", display: "flex", flexDirection: "column", gap: 12}}>
                <div className="small" style={{fontWeight: 600, color: "var(--text)"}}>
                  {lang === "id" ? "Bagikan Verifikasi:" : "Share Verification:"}
                </div>
                <div className="row gap-2" style={{width: "100%"}}>
                  <a
                    href={linkedInUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      flex: 1,
                      textDecoration: "none",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 16px",
                      fontSize: 13,
                      fontWeight: 600,
                      background: "#0077B5",
                      color: "white",
                      borderRadius: "var(--r-md)",
                      justifyContent: "center",
                      boxShadow: "0 2px 8px rgba(0, 119, 181, 0.15)",
                      transition: "transform 0.1s ease"
                    }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    <svg viewBox="0 0 24 24" width={14} height={14} fill="currentColor">
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.779-1.75-1.75s.784-1.75 1.75-1.75 1.75.779 1.75 1.75-.784 1.75-1.75 1.75zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                    </svg>
                    {lang === "id" ? "Tambah ke LinkedIn" : "Add to LinkedIn"}
                  </a>

                  <a
                    href={tweetUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      flex: 1,
                      textDecoration: "none",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 16px",
                      fontSize: 13,
                      fontWeight: 600,
                      background: "#000000",
                      color: "white",
                      borderRadius: "var(--r-md)",
                      justifyContent: "center",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                      transition: "transform 0.1s ease"
                    }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    <svg viewBox="0 0 24 24" width={12} height={12} fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    {lang === "id" ? "Bagikan di X" : "Share on X"}
                  </a>
                </div>
              </div>

              {/* Primary actions */}
              <div className="row gap-2" style={{padding: 16, background: "var(--g-10)"}}>
                {result.pdfUrl ? (
                  <a className="btn-primary" style={{flex: 1, textDecoration:"none", display:"inline-flex", alignItems:"center", gap:6, padding:"7px 12px", fontSize:13, fontWeight:500, background:"var(--program-color, #B84E2A)", color:"white", borderRadius:"var(--r-md)", border:"1px solid transparent", justifyContent:"center"}} href={result.pdfUrl} target="_blank" rel="noopener noreferrer">
                    <Icon name="download" size={14}/>{lang==="id"?"Download PDF":"Download PDF"}
                  </a>
                ) : (
                  <Btn variant="primary" style={{flex: 1}} disabled icon={<Icon name="download" size={14}/>}>
                    {lang==="id"?"PDF sedang dipersiapkan":"PDF is being prepared"}
                  </Btn>
                )}
                <Btn variant="secondary" icon={<Icon name="copy" size={14}/>} onClick={()=>{
                  navigator.clipboard?.writeText(verifyUrl);
                  toast.success(lang==="id"?"Tautan disalin":"Link copied");
                }}>
                  {lang==="id"?"Salin Tautan Verifikasi":"Copy Verification Link"}
                </Btn>
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={{padding:"16px 32px", borderTop:"1px solid var(--border)", color:"var(--text-tertiary)"}} className="row between tiny">
        <span>© {new Date().getFullYear()} {orgName}</span>
        {orgName !== "Gladhy" && <PoweredByGladhy />}
      </div>
    </div>
  );
}

// ── Public Collection Registry (Sprint D) ──────────────────────────────────
// Clean public URL: /?collection=<slug>. Shows a collection as a verifiable
// registry: program, issued/claimed counts, and the trainings it contains.
function PublicCollectionScreen() {
  const orgName = useOrgName()
  const { lang, navigate, pageState } = useApp();
  const id = lang === "id";
  const slug = pageState?.slug || "";

  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState(null);
  const [errorMsg, setErrorMsg] = React.useState("");

  React.useEffect(() => {
    if (!slug) { setLoading(false); return; }
    let cancelled = false;
    getPublicCollection(slug)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => { if (!cancelled) setErrorMsg(err?.response?.data?.error || err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  const fmt = (dt) => dt ? new Date(dt).toLocaleDateString(id ? "id-ID" : "en-US", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", display: "flex", flexDirection: "column" }}>
      <div style={{ background: "#0B1417", color: "white", padding: "14px 32px" }} className="row between">
        <div className="row gap-3">
          <BrandLogo size={28} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{orgName}</div>
            <div style={{ fontSize: 11, opacity: 0.8 }}>{id ? "Registri Kredensial Publik" : "Public Credential Registry"}</div>
          </div>
        </div>
        <button className="btn btn-ghost btn-sm" style={{ color: "white" }} onClick={() => navigate("login")}>
          {id ? "Masuk" : "Sign in"}
        </button>
      </div>

      <div style={{ flex: 1, maxWidth: 720, margin: "0 auto", width: "100%", padding: "40px 24px" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 48, color: "var(--text-tertiary)" }}>{id ? "Memuat…" : "Loading…"}</div>
        ) : errorMsg || !data ? (
          <div style={{ textAlign: "center", padding: 48 }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🔍</div>
            <h2 className="h2">{id ? "Koleksi tidak ditemukan" : "Collection not found"}</h2>
            <p style={{ color: "var(--text-tertiary)" }}>{errorMsg || (id ? "Periksa kembali tautan." : "Please check the link.")}</p>
          </div>
        ) : (
          <>
            <div className="eyebrow" style={{ marginBottom: 6 }}>{data.programName} · Level {data.programLevel}</div>
            <h1 className="h1" style={{ marginBottom: 8 }}>{data.name}</h1>
            {data.description && <p style={{ color: "var(--text-secondary)", marginBottom: 24 }}>{data.description}</p>}

            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 16, marginBottom: 32 }}>
              <div style={{ border: "1px solid var(--border)", borderRadius: 12, padding: 20, textAlign: "center" }}>
                <div style={{ fontSize: 32, fontWeight: 800, color: "var(--program-color, #B84E2A)" }}>{data.issuedCount}</div>
                <div style={{ fontSize: 13, color: "var(--text-tertiary)" }}>{id ? "Sertifikat Diterbitkan" : "Certificates Issued"}</div>
              </div>
              <div style={{ border: "1px solid var(--border)", borderRadius: 12, padding: 20, textAlign: "center" }}>
                <div style={{ fontSize: 32, fontWeight: 800, color: "#16a34a" }}>{data.claimedCount}</div>
                <div style={{ fontSize: 13, color: "var(--text-tertiary)" }}>{id ? "Telah Diklaim" : "Claimed"}</div>
              </div>
            </div>

            {data.trainings?.length > 0 && (
              <>
                <h3 className="h3" style={{ marginBottom: 12 }}>{id ? "Pelatihan dalam koleksi ini" : "Trainings in this collection"}</h3>
                <TableWrap>
                  <table>
                    <thead>
                      <tr>
                        <th>Training</th>
                        <th>{id ? "Tanggal" : "Date"}</th>
                        <th style={{ textAlign: "center" }}>{id ? "Diterbitkan" : "Issued"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.trainings.map((tr, i) => (
                        <tr key={i}>
                          <td style={{ fontWeight: 500 }}>{tr.name}</td>
                          <td style={{ color: "var(--text-tertiary)", fontSize: 13 }}>{fmt(tr.trainingDate)}</td>
                          <td style={{ textAlign: "center" }}>{tr.issued}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableWrap>
              </>
            )}
          </>
        )}
      </div>

      <div style={{ padding: "16px 32px", borderTop: "1px solid var(--border)", color: "var(--text-tertiary)" }} className="row between tiny">
        <span>© {new Date().getFullYear()} {orgName}</span>
        {orgName !== "Gladhy" && <PoweredByGladhy />}
      </div>
    </div>
  );
}

export { ClaimScreen, AdminCertSendScreen, VerifyPublicScreen, PublicCollectionScreen }
